import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FiSend, FiTrash2, FiMessageSquare } from "react-icons/fi";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.dhoorbin.com";

function newSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 12);
}

function getOrCreateSessionId() {
  let id = localStorage.getItem("automationSessionId");
  if (!id) {
    id = newSessionId();
    localStorage.setItem("automationSessionId", id);
  }
  return id;
}

const markdownComponents = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  strong: ({ node, ...props }) => (
    <strong className="font-semibold" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />
  ),
  li: ({ node, ...props }) => <li {...props} />,
  h1: ({ node, ...props }) => (
    <h1 className="text-base font-bold mb-2 mt-1" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-sm font-bold mb-2 mt-1" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-semibold mb-1 mt-1" {...props} />
  ),
  hr: () => <hr className="my-2 border-gray-200" />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code
        className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs"
        {...props}
      />
    ) : (
      <code
        className="block bg-gray-100 text-gray-800 p-2 rounded text-xs overflow-x-auto"
        {...props}
      />
    ),
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto mb-2">
      <table
        className="min-w-full text-xs border border-gray-200 rounded-md overflow-hidden"
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => (
    <tr className="border-b border-gray-200 last:border-0" {...props} />
  ),
  th: ({ node, ...props }) => (
    <th
      className="text-left font-semibold px-2 py-1 border-r border-gray-200 last:border-0"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      className="px-2 py-1 border-r border-gray-200 last:border-0 align-top"
      {...props}
    />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-blue-600 underline"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
};

function MessageContent({ role, content }) {
  if (role === "user") {
    return <span className="whitespace-pre-wrap">{content}</span>;
  }
  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

const SUGGESTIONS = [
  "Create a hotel invoice",
  "Show this month's booking revenue",
  "Onboard a hotel from a website link",
  "Find a customer's bookings",
];

export default function AutomationChat() {
  const [sessionId, setSessionId] = useState(getOrCreateSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/admin/ai/hotel/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: text }),
      });
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.ok ? data.reply : data.error || "Something went wrong.",
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Failed to reach assistant." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (clearing) return;
    const ok = window.confirm(
      "Clear this conversation? The chat history for this session will be permanently deleted.",
    );
    if (!ok) return;

    setClearing(true);
    try {
      await fetch(
        `${API_BASE}/admin/ai/hotel/clean?sessionId=${encodeURIComponent(sessionId)}`,
        { method: "DELETE" },
      );
    } catch (err) {
      // non-blocking: still reset the UI
    } finally {
      const fresh = newSessionId();
      localStorage.setItem("automationSessionId", fresh);
      setSessionId(fresh);
      setMessages([]);
      setClearing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col w-full max-w-full h-[calc(100lvh-120px)] border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* HEADER */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
            <FiMessageSquare className="text-base" />
          </div>
          <div>
            <p className="font-semibold text-sm leading-tight">
              Booking Assistant
            </p>
            <p className="text-[11px] text-white/60 leading-tight">
              Hotel &amp; tour bookings, invoices, onboarding
            </p>
          </div>
        </div>
        <button
          onClick={handleClear}
          disabled={clearing}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 duration-200 disabled:opacity-50"
        >
          <FiTrash2 className="text-sm" />
          {clearing ? "Clearing..." : "Clear"}
        </button>
      </div>

      {/* MESSAGES */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-3">
              <FiMessageSquare className="text-xl" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              How can I help with bookings today?
            </p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              Ask me to create or edit an invoice, check revenue, or onboard a
              hotel from a link.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:text-blue-600 duration-200"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${
              m.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm shadow-sm ${
                m.role === "user"
                  ? "bg-blue-600 text-white rounded-br-md"
                  : "bg-white text-gray-800 border border-gray-200 rounded-bl-md"
              }`}
            >
              <MessageContent role={m.role} content={m.content} />
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-gray-200 rounded-bl-md">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* INPUT */}
      <div className="border-t border-gray-200 p-3 bg-white">
        <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-blue-200">
          <textarea
            className="flex-1 bg-transparent resize-none text-sm outline-none max-h-40"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
          />
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="shrink-0 w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiSend className="text-base" />
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1.5 text-center">
          Press Enter to send, Shift + Enter for a new line
        </p>
      </div>
    </div>
  );
}
